import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { OrderConversation } from '../components/OrderConversation';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  allowedTransitions,
  COURIER_GST,
  formatMinor,
  marginPercent,
  rankVendors,
  toMinor,
  type Order,
  type OrderStatus,
  type Vendor,
  type VendorScore,
} from '@eprintsmart/shared';
import {
  assignVendor,
  downloadArtworkOriginal,
  downloadInvoicePdf,
  loadArtwork,
  loadOrder,
  overrideArtworkWarning,
  refundOrder,
  subscribeToOpenJobCounts,
  subscribeToVendors,
  updateOrderStatus,
  type ArtworkFileView,
} from '../services/orders';
import { paymentsForOrder, type PaymentRow } from '../services/payments';
import { loadProduct } from '../services/catalog';

export function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [artwork, setArtwork] = useState<ArtworkFileView[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [jobCounts, setJobCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundNote, setRefundNote] = useState<string | null>(null);
  const [payments, setPayments] = useState<PaymentRow[]>([]);

  const refresh = useCallback(async () => {
    if (!id) return;
    const [o, files, pays] = await Promise.all([
      loadOrder(id),
      loadArtwork(id).catch(() => []),
      paymentsForOrder(id).catch(() => []),
    ]);
    setOrder(o);
    setArtwork(files);
    setPayments(pays);
    setLoading(false);
  }, [id]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => subscribeToVendors(setVendors), []);
  useEffect(() => subscribeToOpenJobCounts(setJobCounts), []);

  // Mirrors alreadyRefundedMinor on the server: REFUNDED rows only, so a
  // refund Razorpay later failed (REFUND_FAILED) stops counting against the
  // headroom, exactly as it does there.
  const refundedSoFar = payments
    .filter((p) => p.status === 'REFUNDED')
    .reduce((sum, p) => sum + p.amountMinor, 0);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (!order) return <Alert severity="error">No order with id &ldquo;{id}&rdquo;.</Alert>;

  const transitions = allowedTransitions(order.status, 'admin');
  const daysAvailable = order.promisedBy
    ? Math.max(0, Math.ceil((order.promisedBy - Date.now()) / 86_400_000))
    : undefined;

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h1">{order.orderNumber}</Typography>
          <Typography variant="caption" color="text.secondary">
            {order.shippingAddress?.name} · {order.customerEmail || 'guest'} ·{' '}
            {new Date(order.createdAt).toLocaleString('en-IN')}
          </Typography>
        </Box>
        {order.paymentId && (
          <Button size="small" variant="outlined" disabled={busy} onClick={() => run(() => downloadInvoicePdf(order.id))}>
            Invoice PDF
          </Button>
        )}
        <Chip label={order.status.replace(/_/g, ' ')} variant="outlined" color="primary" />
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {order.pendingRefund?.status === 'WAITING_FUNDS' && (
        <Alert severity="info">
          A refund of <strong>{formatMinor(order.pendingRefund.amountMinor)}</strong> is waiting for
          Razorpay funds. Refunds are paid from the Razorpay balance, which this account settles to
          the bank same-day — so it is usually empty. Requested{' '}
          {new Date(order.pendingRefund.requestedAt).toLocaleString('en-IN')} ·{' '}
          {order.pendingRefund.attempts} attempt{order.pendingRefund.attempts === 1 ? '' : 's'} so far.
          The customer is not told until the money has moved.{' '}
          {/* This used to promise that adding refund credits, or simply waiting,
              would clear it within the hour. On 24 Sep 2026 ₹500 of refund
              credits were added and the API went on refusing; the Money page
              shows the live balance rather than repeating the assumption. */}
          <Link component={RouterLink} to="/money">See the live balance on the Money page.</Link>
        </Alert>
      )}
      {order.pendingRefund?.status === 'FAILED' && (
        <Alert
          severity="error"
          action={<Button size="small" component={RouterLink} to="/money">Retry or cancel</Button>}
        >
          The queued refund of <strong>{formatMinor(order.pendingRefund.amountMinor)}</strong> was
          declined by Razorpay for a reason other than balance and needs a person:{' '}
          {order.pendingRefund.lastError ?? 'no reason given'}. Nothing has been paid. It will not
          retry on its own — a refund declined on its merits must not be resent automatically.
        </Alert>
      )}
      {refundNote && (
        <Alert severity="success" onClose={() => setRefundNote(null)}>{refundNote}</Alert>
      )}

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start">
        <Stack spacing={2} sx={{ flex: 2, width: '100%' }}>
          <ItemsPanel
            order={order}
            artwork={artwork}
            busy={busy}
            onOverride={(itemId, reason) =>
              run(() => overrideArtworkWarning(order.id, itemId, reason))
            }
            onDownload={(fileId) => run(() => downloadArtworkOriginal(order.id, fileId))}
          />

          <AssignmentPanel
            order={order}
            vendors={vendors}
            jobCounts={jobCounts}
            daysAvailable={daysAvailable}
            busy={busy}
            onAssign={(assignments, note) => run(() => assignVendor({ orderId: order.id, assignments, note }))}
          />

          {/* The conversation, below the work. Anything written here reaches
              the customer's own order page and emails them a link to it. */}
          <OrderConversation orderId={order.id} customerEmail={order.customerEmail} />
        </Stack>

        <Stack spacing={2} sx={{ flex: 1, width: '100%' }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Move this order</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Stack spacing={1}>
              {transitions.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No further transitions from {order.status.replace(/_/g, ' ')}.
                </Typography>
              )}
              {/*
                * "Assign to vendor" used to be a button here and no longer is,
                * because it moved the order without creating the vendor's job —
                * an order could read IN_PRODUCTION while the vendor's portal was
                * empty. Removing it silently would just send somebody hunting,
                * so say where the real control is.
                */}
              {order.status === 'ARTWORK_REVIEW' && (
                <Typography variant="body2" color="text.secondary">
                  To assign a vendor, use the <strong>Assignment</strong> panel on the left —
                  it creates the vendor&rsquo;s job and moves the order together. Setting the
                  status here would leave them with nothing to print.
                </Typography>
              )}
              {transitions.map((t) =>
                // Refunding moves real money, so it never rides on a plain
                // status button — it needs an amount and a reason.
                t.to === 'REFUNDED' ? (
                  <Button
                    key={t.to}
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={busy || !order.paymentId || order.pendingRefund?.status === 'WAITING_FUNDS'}
                    onClick={() => setRefunding(true)}
                  >
                    Refund…
                  </Button>
                ) : (
                  <Button
                    key={t.to}
                    size="small"
                    variant="outlined"
                    disabled={busy}
                    onClick={() => run(() => updateOrderStatus(order.id, t.to as OrderStatus))}
                  >
                    {t.label}
                  </Button>
                ),
              )}
            </Stack>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Delivery</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="body2">
              {order.shippingAddress?.line1}
              {order.shippingAddress?.line2 ? `, ${order.shippingAddress.line2}` : ''}
              <br />
              {order.shippingAddress?.city}, {order.shippingAddress?.state}{' '}
              {order.shippingAddress?.pincode}
              <br />
              {order.shippingAddress?.phone}
            </Typography>
            {order.shippingAddress?.gstin && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                GSTIN {order.shippingAddress.gstin}
              </Typography>
            )}
            {order.promisedBy && (
              <Typography variant="body2" sx={{ mt: 1.5 }}>
                Promised by{' '}
                <strong>{new Date(order.promisedBy).toLocaleDateString('en-IN', { day: 'numeric', month: 'long' })}</strong>
                {daysAvailable != null && ` · ${daysAvailable} day(s) left`}
              </Typography>
            )}
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Totals</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Row label="Items" value={formatMinor(order.totals.itemsSubtotalMinor)} />
            {order.totals.discountMinor > 0 && (
              <Row label="Discount" value={`−${formatMinor(order.totals.discountMinor)}`} />
            )}
            <Row label="Shipping" value={formatMinor(order.totals.shippingMinor)} />
            <ShippingSplit order={order} />
            <Row label="Taxable" value={formatMinor(order.totals.taxableMinor)} />
            {order.totals.igstMinor > 0
              ? <Row label="IGST" value={formatMinor(order.totals.igstMinor)} />
              : (
                <>
                  <Row label="CGST" value={formatMinor(order.totals.cgstMinor)} />
                  <Row label="SGST" value={formatMinor(order.totals.sgstMinor)} />
                </>
              )}
            {/* Guarded rather than required: orders placed before the charge was
                settled to the rupee have no roundOffMinor on their stored
                totals, and showing "₹0.00" on those would be a claim we cannot
                make. */}
            {!!order.totals.roundOffMinor && (
              <Row
                label="Round off"
                value={`${order.totals.roundOffMinor > 0 ? '+' : '−'}${formatMinor(Math.abs(order.totals.roundOffMinor))}`}
              />
            )}
            <Divider sx={{ my: 1 }} />
            <Row label="Total" value={formatMinor(order.totals.grandTotalMinor)} bold />
          </Paper>

          {/* Where this customer came from. Absent on orders placed before
              25 Sep 2026, when nothing recorded it — which is exactly why the
              first three real orders could never be attributed. */}
          {order.attribution && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>How they found us</Typography>
              <Divider sx={{ mb: 1.5 }} />
              <TouchRow label="This visit" touch={order.attribution.session} />
              {order.attribution.first && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <TouchRow label="First ever visit" touch={order.attribution.first} />
                </>
              )}
            </Paper>
          )}

          {/* What Razorpay actually did with the money, as opposed to what the
              order says it should have cost. Nothing in this console read the
              payments collection before. */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Money</Typography>
            <Divider sx={{ mb: 1.5 }} />
            {payments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No payment has been recorded against this order.
              </Typography>
            ) : (
              <>
                {payments.map((p) => (
                  <Box key={p.id} sx={{ mb: 1.5 }}>
                    <Row
                      label={p.kind === 'REFUND' ? 'Refunded' : p.status === 'FAILED' ? 'Failed attempt' : 'Captured'}
                      value={`${p.kind === 'REFUND' ? '−' : ''}${formatMinor(p.amountMinor)}`}
                      bold={p.kind !== 'REFUND' && p.status !== 'FAILED'}
                    />
                    {p.feeMinor != null && (
                      <Row label="Gateway fee" value={`−${formatMinor(p.feeMinor)}`} />
                    )}
                    {p.netMinor != null && <Row label="Net to bank" value={formatMinor(p.netMinor)} />}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {p.id}{p.method ? ` · ${p.method}` : ''}
                      {p.refundStatus ? ` · Razorpay says ${p.refundStatus}` : ''}
                      {p.settlementId ? ` · settled ${p.settlementId}` : ''}
                    </Typography>
                    {p.errorDescription && (
                      <Typography variant="caption" color="error" display="block">{p.errorDescription}</Typography>
                    )}
                  </Box>
                ))}
                <Divider sx={{ my: 1 }} />
                {/* Remaining refundable, computed the same way the server does:
                    the order total less everything already refunded. */}
                <Row
                  label="Refunded so far"
                  value={formatMinor(refundedSoFar)}
                />
                <Row
                  label="Still refundable"
                  value={formatMinor(Math.max(0, order.totals.grandTotalMinor - refundedSoFar))}
                  bold
                />
              </>
            )}
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>History</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Stack spacing={1}>
              {[...order.timeline].reverse().map((entry, i) => (
                <Box key={i}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(entry.at).toLocaleString('en-IN')} · {entry.byRole}
                  </Typography>
                  <Typography variant="body2">{entry.note ?? entry.status}</Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Stack>
      </Stack>

      <Dialog open={refunding} onClose={() => setRefunding(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Refund {order.orderNumber}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This sends a real refund to Razorpay. Leave the amount blank to refund everything
            still outstanding.
          </Alert>
          <TextField
            fullWidth type="number" label="Amount (₹) — blank for full"
            value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)}
            sx={{ mb: 2 }}
            helperText={`Order total ${formatMinor(order.totals.grandTotalMinor)}`}
          />
          <TextField
            fullWidth multiline minRows={2} label="Reason"
            value={refundReason} onChange={(e) => setRefundReason(e.target.value)}
            placeholder="e.g. Two of five banners arrived creased."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefunding(false)}>Cancel</Button>
          <Button
            variant="contained" color="error"
            disabled={busy || refundReason.trim().length < 3}
            onClick={() => {
              const amount = Number(refundAmount);
              setRefunding(false);
              void run(async () => {
                const result = await refundOrder({
                  orderId: order.id,
                  ...(Number.isFinite(amount) && amount > 0 ? { amountMinor: toMinor(amount) } : {}),
                  reason: refundReason.trim(),
                });
                // Queued and paid are very different outcomes and used to look
                // identical from here.
                setRefundNote(result.queued
                  ? `${formatMinor(result.amountMinor)} is queued — the Razorpay balance could not cover it yet. `
                    + 'The customer has not been told. It goes out on the next capture or hourly sweep.'
                  : `${formatMinor(result.amountMinor)} refunded. Razorpay has accepted it; a normal-speed `
                    + 'refund reaches the customer in 5–7 working days, and the order history will say when it lands.');
              });
              setRefundReason('');
              setRefundAmount('');
            }}
          >
            Issue refund
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/** One arrival, rendered so the channel is the thing you read first. */
function TouchRow({ label, touch }: { label: string; touch: NonNullable<Order['attribution']>['session'] }) {
  const CHANNEL_COLOUR: Record<string, 'success' | 'primary' | 'warning' | 'default'> = {
    'ai-assistant': 'success',
    'organic-search': 'primary',
    paid: 'warning',
    social: 'primary',
    referral: 'default',
    direct: 'default',
  };
  return (
    <Box sx={{ mb: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>{label}</Typography>
        <Chip
          size="small"
          variant="outlined"
          color={CHANNEL_COLOUR[touch.channel] ?? 'default'}
          label={touch.assistant ?? touch.channel.replace('-', ' ')}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block">
        landed on {touch.landingPath}
      </Typography>
      {touch.referrer && (
        <Typography variant="caption" color="text.secondary" display="block">
          from {touch.referrer}
        </Typography>
      )}
      {(touch.utmSource || touch.utmCampaign) && (
        <Typography variant="caption" color="text.secondary" display="block">
          utm: {[touch.utmSource, touch.utmMedium, touch.utmCampaign].filter(Boolean).join(' / ')}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" display="block">
        {new Date(touch.at).toLocaleString('en-IN')}
      </Typography>
    </Box>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 400 }}>{value}</Typography>
    </Box>
  );
}

/**
 * How the shipping line was arrived at, from the quote stored on the order -
 * so a charge can be explained after the rate cards change. Orders placed
 * before 12 Oct 2026 have no `service` and were all freight.
 */
function ShippingSplit({ order }: { order: Order }) {
  const q = order.shipping;
  if (!q || !order.totals.shippingMinor) return null;
  const rs = (n: number) => `₹${n.toFixed(2)}`;
  const margin = `+${Math.round((q.markup - 1) * 100)}% margin`;
  const lines = q.service === 'courier'
    ? [
        `Courier · zone ${q.courierZone} · ${q.chargeableKg} kg`,
        q.carrierFreight != null
          ? `${rs(q.carrierFreight)} card + ${Math.round(COURIER_GST * 100)}% GST ${margin}`
          : null,
      ]
    : [
        `Freight · zone ${q.zone} · ${q.chargeableKg} kg × ${rs(q.ratePerKg)}/kg`,
        q.carrierFreight != null ? `${rs(q.carrierFreight)} card ${margin}` : margin,
      ];
  return (
    <Box sx={{ mt: -0.25, mb: 0.75 }}>
      {[...lines, !q.zoneMatched ? 'PIN not matched - fallback zone charged' : null, q.estimated ? 'Some weights estimated' : null]
        .filter(Boolean)
        .map((l) => (
          <Typography key={l} variant="caption" color="text.secondary" display="block">{l}</Typography>
        ))}
    </Box>
  );
}

/* ------------------------------------------------------------------ */

function ItemsPanel({
  order,
  artwork,
  busy,
  onOverride,
  onDownload,
}: {
  order: Order;
  artwork: ArtworkFileView[];
  busy: boolean;
  onOverride: (itemId: string, reason: string) => void;
  onDownload: (artworkFileId: string) => void;
}) {
  const [overriding, setOverriding] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Items and artwork</Typography>
      <Divider sx={{ mb: 1.5 }} />

      {order.items.map((item) => {
        const files = artwork.filter((f) => f.orderItemId === item.id);
        const failed = files.some((f) => f.status === 'FAIL');

        return (
          <Box key={item.id} sx={{ mb: 2.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontWeight: 600 }}>{item.productName}</Typography>
              <Chip size="small" variant="outlined" label={`${item.qty}`} />
              <Chip
                size="small"
                variant="outlined"
                label={item.artworkStatus}
                color={
                  item.artworkStatus === 'FAIL' ? 'error'
                  : item.artworkStatus === 'WARN' ? 'warning'
                  : item.artworkStatus === 'NONE' ? 'default' : 'success'
                }
              />
              {item.vendorId && <Chip size="small" label={`→ ${item.vendorId}`} color="primary" variant="outlined" />}
            </Stack>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {item.selectedOptions.map((o) => `${o.groupName}: ${o.valueName}`).join(' · ')}
            </Typography>
            {item.printInstructions && (
              <Box sx={{ mt: 0.75, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                  Print instructions
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {item.printInstructions}
                </Typography>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" display="block">
              {item.printSpec.trimWidthMm} × {item.printSpec.trimHeightMm} mm ·{' '}
              {item.printSpec.bleedMm} mm bleed · {item.printSpec.sides} side(s) ·{' '}
              {formatMinor(item.price.subtotalMinor)}
            </Typography>

            {files.map((file) => (
              <Box key={file.id} sx={{ mt: 1, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  {file.previewUrl && (
                    <Box
                      component="img"
                      src={file.previewUrl}
                      alt=""
                      sx={{ width: 88, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                    />
                  )}
                  <Box>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {file.originalFilename}
                    </Typography>
                    {/* The press must know a file was upscaled by AI, and an
                        unchosen or declined one must not be mistaken for the
                        artwork. */}
                    {file.enhancedFrom && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ fontWeight: 700 }}
                        color={file.pendingChoice || file.discarded ? 'text.secondary' : 'info.main'}
                      >
                        {file.pendingChoice
                          ? 'AI-enhanced · waiting for the customer to choose'
                          : file.discarded
                            ? 'AI-enhanced · declined by the customer, not for print'
                            : 'AI-enhanced · chosen by the customer'}
                      </Typography>
                    )}
                    <Button size="small" sx={{ px: 0, minWidth: 0 }} disabled={busy} onClick={() => onDownload(file.id)}>
                      Download original
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {file.widthMm ? `${Math.round(file.widthMm)} × ${Math.round(file.heightMm ?? 0)} mm` : ''}
                      {file.effectiveDpi ? ` · ${file.effectiveDpi} DPI` : ''}
                    </Typography>
                    {file.checks
                      .filter((c) => c.severity !== 'pass')
                      .map((c, i) => (
                        <Typography
                          key={i}
                          variant="caption"
                          display="block"
                          color={c.severity === 'fail' ? 'error.main' : 'warning.main'}
                        >
                          {c.message}
                        </Typography>
                      ))}
                  </Box>
                </Stack>
              </Box>
            ))}

            {item.artworkStatus === 'WARN' && (
              <Button size="small" sx={{ mt: 1 }} disabled={busy} onClick={() => setOverriding(item.id)}>
                Accept this warning
              </Button>
            )}
            {failed && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Preflight failed. This cannot go to a vendor until the customer supplies a corrected
                file.
              </Alert>
            )}
          </Box>
        );
      })}

      <Dialog open={!!overriding} onClose={() => setOverriding(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Accept the artwork warning</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            This is recorded against the order. Say why the job can print as supplied.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Logo is 280 DPI but prints at 25 mm, checked with the customer."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverriding(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={reason.trim().length < 3}
            onClick={() => {
              onOverride(overriding!, reason.trim());
              setOverriding(null);
              setReason('');
            }}
          >
            Accept and continue
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

/* ------------------------------------------------------------------ */

function AssignmentPanel({
  order,
  vendors,
  jobCounts,
  daysAvailable,
  busy,
  onAssign,
}: {
  order: Order;
  vendors: Vendor[];
  jobCounts: Record<string, number>;
  daysAvailable?: number;
  busy: boolean;
  onAssign: (
    assignments: { orderItemId: string; vendorId: string; vendorCostMinor?: number }[],
    note?: string,
  ) => void;
}) {
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [costOverride, setCostOverride] = useState<Record<string, string>>({});
  /** Items an admin has opened back up to move to a different vendor. */
  const [reassigning, setReassigning] = useState<string[]>([]);

  // Category ids per product on this order, for the capability match below.
  // Read from the catalogue rather than the order, because an OrderItem has
  // never carried them.
  const [categoriesByProduct, setCategoriesByProduct] = useState<Record<string, string[]>>({});
  useEffect(() => {
    let live = true;
    const ids = [...new Set(order.items.map((i) => i.productId))];
    Promise.all(ids.map((id) => loadProduct(id)))
      .then((loaded) => {
        if (!live) return;
        const map: Record<string, string[]> = {};
        loaded.forEach((p, i) => { if (p) map[ids[i]!] = p.product.categoryIds ?? []; });
        setCategoriesByProduct(map);
      })
      .catch(() => { /* ranking degrades to cost-only, as it did before */ });
    return () => { live = false; };
  }, [order.items]);

  // Rank once per item, and re-rank when vendors or load change.
  const rankings = useMemo(() => {
    const map: Record<string, VendorScore[]> = {};
    for (const item of order.items) {
      map[item.id] = rankVendors({
        item,
        /*
         * The product's real categories. This passed `[item.productId]`, but
         * capabilities and category rate cards are keyed by CATEGORY — so no
         * vendor could ever score as capable and every category rate was dead
         * weight. The ranking silently recommended on cost alone.
         */
        categoryIds: categoriesByProduct[item.productId] ?? [],
        vendors,
        daysAvailable,
        openJobsByVendor: jobCounts,
      });
    }
    return map;
  }, [order.items, vendors, daysAvailable, jobCounts]);

  // Preselect the top suggestion for anything not already assigned.
  useEffect(() => {
    setChoice((current) => {
      const next = { ...current };
      for (const item of order.items) {
        if (next[item.id]) continue;
        if (item.vendorId) { next[item.id] = item.vendorId; continue; }
        const best = rankings[item.id]?.find((r) => r.capable);
        if (best) next[item.id] = best.vendor.id;
      }
      return next;
    });
  }, [order.items, rankings]);

  /*
   * A service line is never produced, so it never gets a vendor. Counting it
   * as unassigned would hold this panel open on every passport order,
   * demanding a vendor for the editing charge and never being satisfied.
   */
  const printable = order.items.filter((i) => !i.isService);
  const unassigned = printable.filter((i) => !i.vendorId || reassigning.includes(i.id));

  if (unassigned.length === 0) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Vendor assignment</Typography>
        <Divider sx={{ mb: 1.5 }} />
        {/*
          * Assigned is not final. Work routed to ourselves automatically is the
          * normal case now, and moving a line to a real supplier afterwards was
          * previously impossible without walking the whole order back to
          * artwork review. A job the vendor has already accepted and started is
          * still refused server-side.
          */}
        <Stack spacing={1}>
          {printable.map((item) => (
            <Stack key={item.id} direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {item.productName} × {item.qty}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {vendors.find((v) => v.id === item.vendorId)?.name ?? item.vendorId}
                  {item.vendorStatus ? ` · ${item.vendorStatus.toLowerCase().replace(/_/g, ' ')}` : ''}
                </Typography>
              </Typography>
              <Button
                size="small"
                onClick={() => setReassigning((r) => [...r, item.id])}
                disabled={busy}
              >
                Reassign
              </Button>
            </Stack>
          ))}
        </Stack>
      </Paper>
    );
  }

  const submit = () => {
    const assignments = unassigned
      .filter((i) => choice[i.id])
      .map((i) => {
        const raw = costOverride[i.id];
        const parsed = raw ? Number(raw) : NaN;
        return {
          orderItemId: i.id,
          vendorId: choice[i.id]!,
          ...(Number.isFinite(parsed) && parsed > 0 ? { vendorCostMinor: toMinor(parsed) } : {}),
        };
      });
    if (assignments.length) onAssign(assignments);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Vendor assignment</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Suggestions are ranked on cost, turnaround against the promised date, on-time record and
        current load. Confirm or change each one.
      </Typography>
      <Divider sx={{ mb: 1.5 }} />

      {unassigned.map((item) => {
        const ranked = rankings[item.id] ?? [];
        const selected = ranked.find((r) => r.vendor.id === choice[item.id]);
        const margin = selected ? marginPercent(item.price.subtotalMinor, selected.costMinor) : null;

        return (
          <Box key={item.id} sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>{item.productName} × {item.qty}</Typography>

            {ranked.length === 0 ? (
              <Alert severity="warning">
                No active vendors. Add one before assigning this order.
              </Alert>
            ) : (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={44} />
                      <TableCell>Vendor</TableCell>
                      <TableCell align="right">Our cost</TableCell>
                      <TableCell align="right">Margin</TableCell>
                      <TableCell align="right">Score</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ranked.slice(0, 5).map((r) => (
                      <TableRow
                        key={r.vendor.id}
                        hover
                        selected={choice[item.id] === r.vendor.id}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setChoice((c) => ({ ...c, [item.id]: r.vendor.id }))}
                      >
                        <TableCell>
                          <input
                            type="radio"
                            checked={choice[item.id] === r.vendor.id}
                            onChange={() => setChoice((c) => ({ ...c, [item.id]: r.vendor.id }))}
                          />
                        </TableCell>
                        <TableCell>
                          {r.vendor.name}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {r.vendor.turnaroundDays}d turnaround
                            {jobCounts[r.vendor.id] ? ` · ${jobCounts[r.vendor.id]} open` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {r.costMinor != null ? formatMinor(r.costMinor) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {marginPercent(item.price.subtotalMinor, r.costMinor) != null
                            ? `${marginPercent(item.price.subtotalMinor, r.costMinor)}%`
                            : '—'}
                        </TableCell>
                        <TableCell align="right">{r.score}</TableCell>
                        <TableCell>
                          {r.warnings.map((w, i) => (
                            <Tooltip key={i} title={w}>
                              <Chip size="small" color="warning" variant="outlined" label="!" sx={{ mr: 0.5 }} />
                            </Tooltip>
                          ))}
                          <Typography variant="caption" color="text.secondary">
                            {r.reasons[0]}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                  <TextField
                    size="small"
                    label="Override cost (₹)"
                    type="number"
                    value={costOverride[item.id] ?? ''}
                    onChange={(e) => setCostOverride((c) => ({ ...c, [item.id]: e.target.value }))}
                    sx={{ width: 190 }}
                    helperText={
                      selected?.costMinor != null
                        ? `Rate card: ${formatMinor(selected.costMinor)}`
                        : 'No rate card entry — a cost is required'
                    }
                  />
                  {margin != null && (
                    <Typography variant="body2" color={margin < 15 ? 'warning.main' : 'success.main'}>
                      Margin {margin}%
                      {margin < 15 && ' — thin'}
                    </Typography>
                  )}
                </Stack>
              </>
            )}
          </Box>
        );
      })}

      <Button variant="contained" color="secondary" disabled={busy} onClick={submit}>
        Assign {unassigned.length} item{unassigned.length === 1 ? '' : 's'}
      </Button>
    </Paper>
  );
}
